<script setup lang="ts">
import {
    CircleCheck,
    Hash,
    ImagePlus,
    Languages,
    RefreshCw,
} from '@lucide/vue';
import PlatformIcon from '@/components/marketing/shared/PlatformIcon.vue';
import SectionHeading from '@/components/marketing/shared/SectionHeading.vue';

const capabilities = [
    {
        icon: ImagePlus,
        text: 'Branded promotional graphics and illustrations',
    },
    {
        icon: Hash,
        text: 'Hashtags and calls to action suggested per platform',
    },
    {
        icon: RefreshCw,
        text: 'Repurpose an article, video or product page into new posts',
    },
    {
        icon: Languages,
        text: 'Generate and translate content for more than one language',
    },
    {
        icon: CircleCheck,
        text: 'Ask for several variations and pick the one you like',
    },
];
</script>

<template>
    <section id="content" class="scroll-mt-24 py-16 sm:py-20">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
                title="Content that sounds like you."
                eyebrow="Content creation"
                description="Your assistant writes in your brand voice, using what it learned from your website and your feedback."
            />

            <div
                class="mt-12 grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-center"
            >
                <div
                    class="bg-card border-border mx-auto w-full max-w-sm rounded-2xl border p-5 shadow-sm"
                >
                    <div class="flex items-center justify-between gap-3">
                        <PlatformIcon platform="instagram" />
                        <span
                            class="bg-secondary/10 text-muted-foreground rounded-full px-2 py-0.5 text-xs"
                        >
                            Ready for review
                        </span>
                    </div>

                    <div
                        class="bg-muted mt-4 flex aspect-square w-full items-center justify-center rounded-xl"
                    >
                        <ImagePlus
                            class="text-muted-foreground/50 size-10"
                            aria-hidden="true"
                        />
                    </div>

                    <p class="mt-4 text-sm leading-relaxed">
                        Your next favourite thing is almost here. We have been
                        working on something for people who want great marketing
                        without the busywork.
                    </p>
                    <p class="text-muted-foreground mt-2 text-sm">
                        #smallbusiness #productlaunch #behindthescenes
                    </p>

                    <div
                        class="border-border mt-4 flex items-center gap-2 border-t pt-4 text-xs"
                    >
                        <CircleCheck
                            class="text-secondary size-4"
                            aria-hidden="true"
                        />
                        One of five variations you can choose from
                    </div>
                </div>

                <ul class="grid gap-3">
                    <li
                        v-for="item in capabilities"
                        :key="item.text"
                        class="flex items-start gap-3 text-sm sm:text-base"
                    >
                        <component
                            :is="item.icon"
                            class="text-primary mt-0.5 size-4 shrink-0"
                            aria-hidden="true"
                        />
                        {{ item.text }}
                    </li>
                </ul>
            </div>
        </div>
    </section>
</template>
